import { useFormikContext } from "formik";
import { Plus, Trash } from "lucide-react";
import InputField from "@/components/form/InputField";
import {
  CreateField,
  FieldSelectOption,
  StandardFieldDatabaseType,
} from "@/types/breeze/Field";
import CheckboxField from "../form/CheckboxField";
import { Button, Select, Flex, Text, Grid, Card, Box } from "@radix-ui/themes";
import * as Yup from "yup";

type FormContextType = CreateField;

export const selectValidationSchema = {
  options: Yup.object()
    .nullable()
    .shape({
      select: Yup.object()
        .nullable()
        .shape({
          addon_before: Yup.string().nullable(),
          addon_after: Yup.string().nullable(),
          placeholder: Yup.string().nullable(),
          multiple: Yup.boolean().nullable(),
          options: Yup.array()
            .of(
              Yup.object().shape({
                label: Yup.string().required("Option label is required"),
                value: Yup.string().required("Option value is required"),
              }),
            )
            .min(1, "At least one option is required"),
        }),
    }),
};

export default function SelectDisplayOptions() {
  const { values, setFieldValue } = useFormikContext<FormContextType>();

  const getInputType = () => {
    switch (values.database_type) {
      case StandardFieldDatabaseType.INTEGER:
      case StandardFieldDatabaseType.DECIMAL:
        return "number";
      default:
        return "text";
    }
  };

  const formatValue = (value: string): string => {
    switch (values.database_type) {
      case StandardFieldDatabaseType.INTEGER:
        return value ? Math.round(Number(value)).toString() : "";
      case StandardFieldDatabaseType.DECIMAL:
        return value ? Number(value).toString() : "";
      case StandardFieldDatabaseType.BOOLEAN:
        return value === "true" ? "true" : "false";
      default:
        return value;
    }
  };

  const addOption = () => {
    const currentOptions = values.options?.select?.options || [];
    const defaultValue =
      values.database_type === StandardFieldDatabaseType.BOOLEAN ? "false" : "";
    setFieldValue("options.select.options", [
      ...currentOptions,
      { label: "", value: defaultValue },
    ]);
  };

  const handleLabelChange = (index: number, labelValue: string) => {
    const currentOptions = values.options?.select?.options || [];
    const option = currentOptions[index];
    if (option) {
      const updatedOptions = [...currentOptions];
      const defaultValue =
        values.database_type === StandardFieldDatabaseType.BOOLEAN ? "false" : "";
      if (!option.value || option.value === defaultValue || option.value === formatValue(option.label)) {
        updatedOptions[index] = {
          ...option,
          label: labelValue,
          value: formatValue(labelValue),
        };
      } else {
        updatedOptions[index] = {
          ...option,
          label: labelValue,
        };
      }
      setFieldValue("options.select.options", updatedOptions);
    }
  };

  const removeOption = (index: number) => {
    const currentOptions = values.options?.select?.options || [];
    setFieldValue(
      "options.select.options",
      currentOptions.filter((_: FieldSelectOption, i: number) => i !== index),
    );
  };

  const handleValueChange = (index: number, value: string) => {
    const currentOptions = values.options?.select?.options || [];
    const formattedValue = formatValue(value);
    const updatedOptions = [...currentOptions];
    updatedOptions[index] = { ...updatedOptions[index], value: formattedValue };
    setFieldValue("options.select.options", updatedOptions);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <div className="w-1/2">
          <InputField name="options.select.addon_before" label="Addon before" />
        </div>
        <div className="w-1/2">
          <InputField name="options.select.addon_after" label="Addon after" />
        </div>
      </div>

      <InputField name="options.select.placeholder" label="Placeholder" />

      <CheckboxField name="options.select.multiple" label="Multiple" />

      <div className="space-y-2">
        <Flex justify="between" align="center" mb="2">
          <Text size="2" weight="bold">Options</Text>
          <Button type="button" variant="soft" size="1" onClick={addOption}>
            <Plus className="w-4 h-4 mr-1" />
            Add Option
          </Button>
        </Flex>

        {(values.options?.select?.options || []).length === 0 ? (
          <Card style={{ textAlign: "center", padding: "16px", border: "1px dashed var(--gray-5)" }}>
            <Text size="2" color="gray">No options added yet. Click "Add Option" to add one.</Text>
          </Card>
        ) : (
          <div className="space-y-2">
            {/* Header row to prevent duplicate labels on each item */}
            <Grid columns="2" gap="3" style={{ paddingRight: "44px", paddingLeft: "8px" }} mb="1">
              <Text size="1" weight="medium" color="gray">Option Label</Text>
              <Text size="1" weight="medium" color="gray">Database Value</Text>
            </Grid>

            <Flex direction="column" gap="1">
              {(values.options?.select?.options || []).map(
                (option: FieldSelectOption, index: number) => (
                  <Flex key={index} gap="3" align="start">
                    <div className="flex-1">
                      <InputField
                        name={`options.select.options.${index}.label`}
                        options={{ placeholder: "Label (e.g. Active)" }}
                        required
                        onChange={(val) => handleLabelChange(index, val)}
                      />
                    </div>
                    <div className="flex-1">
                      {values.database_type ===
                      StandardFieldDatabaseType.BOOLEAN ? (
                        <Box style={{ width: "100%", height: "40px" }} mt="0">
                          <Select.Root
                            value={option.value}
                            onValueChange={(value) =>
                              handleValueChange(index, value)
                            }
                          >
                            <Select.Trigger style={{ width: "100%" }} />
                            <Select.Content>
                              <Select.Item value="true">True</Select.Item>
                              <Select.Item value="false">False</Select.Item>
                            </Select.Content>
                          </Select.Root>
                        </Box>
                      ) : (
                        <InputField
                          name={`options.select.options.${index}.value`}
                          options={{ placeholder: "Value (e.g. active)" }}
                          required
                          type={getInputType()}
                        />
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      color="red"
                      size="1"
                      onClick={() => removeOption(index)}
                      style={{ alignSelf: "center", marginBottom: "16px" }}
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  </Flex>
                ),
              )}
            </Flex>
          </div>
        )}
      </div>
    </div>
  );
}
